import { AnyFile, FileExt, isFileExt } from "@utils/file-ext";
import * as zip from "@zip.js/zip.js";

const ZIP_NOT_CREATED = "File was not yet extracted from ZIP archive. Call ensureZipBlobExists() first.";

type ZipEntryLike = zip.Entry | {
  filename: string;
  uncompressedSize: number;
  lastModDate: Date;
};

/**
 * A file object that is part of a zip-file.
 */
export class FileFromZip {
  readonly zipFile: AnyFile;
  readonly webkitRelativePath: string;
  readonly size: number;
  readonly name: string;
  readonly lastModified: number;
  readonly type: string;

  #blob: Blob | null = null;
  #blobPromise: Promise<Blob> | null = null;

  constructor(file: AnyFile, entry: ZipEntryLike) {
    this.zipFile = file;
    this.webkitRelativePath = entry.filename;
    this.name = this.webkitRelativePath.split("/")?.pop() ?? this.webkitRelativePath;
    this.size = entry.uncompressedSize;
    this.lastModified = entry.lastModDate.getTime();
    this.type = "application/octet-stream";
  }

  /** Analogue to zip-file.ts, we have a function that ensures that our blob is available */
  async ensureZipBlobExists(): Promise<Blob> {
    try {
      if (this.#blob) {
        return this.#blob;
      }
      const reader = new zip.ZipReader(new zip.BlobReader(this.zipFile));

      // Find the correct entry
      const entries = await reader.getEntries();
      const zipFileEntry = entries.find((entry: zip.Entry) => entry.filename === this.webkitRelativePath);
      if (!zipFileEntry) {
        throw new Error(`Unexpected Error: did not find ${this.webkitRelativePath} in ${this.zipFile.webkitRelativePath}`);
      }

      // Use promise to prevent concurrent zip extraction
      if (!this.#blobPromise) {
        if (!zipFileEntry.getData) {
          throw new Error("Method getData() is not available");
        }
        this.#blobPromise = zipFileEntry.getData(new zip.BlobWriter(this.type));
      }
      this.#blob = await this.#blobPromise;
      // Keeping the blobPromise alive, so it can be awaited again in case of a racing condition

      return this.#blob;
    } catch (err) {
      throw new Error("Could not extract blob from ZIP file.", { cause: err });
    }
  }

  async arrayBuffer(): Promise<ArrayBuffer> {
    const blob = await this.ensureZipBlobExists();
    return blob.arrayBuffer();
  }

  async text(): Promise<string> {
    const blob = await this.ensureZipBlobExists();
    return blob.text();
  }

  async bytes(): ReturnType<typeof Blob.prototype.bytes> {
    const blob = await this.ensureZipBlobExists();
    return new Uint8Array(await blob.arrayBuffer());
  }

  /**
   * The **`slice()`** method of the Blob interface creates and returns a new `Blob` object
   * which contains data from a subset of the blob on which it's called.
   *
   * [MDN Reference](https://developer.mozilla.org/docs/Web/API/Blob/slice)
   */
  slice(start?: number, end?: number, contentType?: string): Blob {
    if (!this.#blob) {
      throw new Error(ZIP_NOT_CREATED);
    }
    return this.#blob.slice(start, end, contentType);
  }

  stream(): ReadableStream {
    if (!this.#blob) {
      throw new Error(ZIP_NOT_CREATED);
    }
    return this.#blob.stream();
  }

  /**
   * Re-Create the FileFromZip after it was serialized
   * @param file FileExt to be re-created
   */
  static createFileFromZipFromSerialized(fileFromZip: FileFromZip): FileFromZip {
    const file: AnyFile = isFileExt(fileFromZip.zipFile)
      ? FileExt.createFileExtFromSerialized(fileFromZip.zipFile)
      : fileFromZip.zipFile;

    const entry: ZipEntryLike = {
      filename: fileFromZip.webkitRelativePath,
      uncompressedSize: fileFromZip.size,
      lastModDate: new Date(fileFromZip.lastModified),
    };
    return new FileFromZip(file, entry);
  }
}

/** Type guard to check if it is a ZipFile, or a serialized ZipFile (e.g. from postMessage()). */
export function isFileFromZip(file: unknown): file is FileFromZip {
  return file instanceof FileFromZip ||
    (file instanceof Object &&
      "zipFile" in file &&
      "type" in file &&
      "name" in file
    );
}
